/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/userAttributes/userAttributesQueries.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */

import http from 'hub-http/clients/apiClient';
import { useCallback } from 'react';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { updateCache } from '../../utils/DataFetchingClientCache';
import { parseJSONOrReturn } from '../../utils/utils.migrated';
const SET_USER_ATTRIBUTE = registerMutation({
  fieldName: 'setUserAttribute',
  args: ['key', 'value'],
  fetcher({
    key,
    value
  }) {
    return http.post(`${BASE_URI_PROGRESS}/user-attributes`, {
      data: {
        key,
        value
      }
    });
  }
});
const DELETE_USER_ATTRIBUTE = registerMutation({
  fieldName: 'deleteUserAttribute',
  args: ['key'],
  fetcher({
    key
  }) {
    return http.delete(`${BASE_URI_PROGRESS}/user-attributes/${key}`);
  }
});
const GET_USER_ATTRIBUTES = registerQuery({
  fieldName: 'userAttributes',
  args: [],
  fetcher() {
    return http.get(`${BASE_URI_PROGRESS}/user-attributes`).then(response => {
      const json = response;
      const newAttributes = {};
      for (const attribute of json) {
        /**
         * Try to parse the value as JSON. If it fails, most likely because it's a plain string, just use the raw value.
         */
        newAttributes[attribute.attributeKey] = parseJSONOrReturn(attribute.attributeValue);
      }
      return newAttributes;
    });
  }
});

/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/userAttributes/userAttributesQueries.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const useUserAttributes = () => {
  const {
    data: userAttributes,
    loading: userAttributesLoading,
    error: userAttributesError
  } = useQuery(GET_USER_ATTRIBUTES);
  return {
    userAttributes,
    userAttributesLoading,
    userAttributesError,
    userAttributesLoaded: !userAttributesLoading && !userAttributesError && !!userAttributes
  };
};

/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/userAttributes/userAttributesQueries.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const useUserAttribute = attributeKey => {
  const {
    userAttributes,
    userAttributesLoading,
    userAttributesError,
    userAttributesLoaded: userAttributeLoaded
  } = useUserAttributes();
  const [set, {
    error: setUserAttributeError
  }] = useMutation(SET_USER_ATTRIBUTE);
  const [unset, {
    error: unsetUserAttributeError
  }] = useMutation(DELETE_USER_ATTRIBUTE);
  const setUserAttribute = useCallback(rawValue => {
    const value = JSON.stringify(rawValue);
    return set({
      variables: {
        value,
        key: attributeKey
      },
      update() {
        updateCache(GET_USER_ATTRIBUTES, attributes => {
          attributes[attributeKey] = rawValue;
        }, {});
      }
    });
  }, [attributeKey, set]);
  const unsetUserAttribute = useCallback(() => {
    return unset({
      variables: {
        key: attributeKey
      },
      update() {
        updateCache(GET_USER_ATTRIBUTES, attributes => {
          delete attributes[attributeKey];
        }, {});
      }
    });
  }, [attributeKey, unset]);
  return {
    userAttribute: userAttributes ? userAttributes[attributeKey] || null : null,
    userAttributeLoading: userAttributesLoading,
    setUserAttribute,
    unsetUserAttribute,
    userAttributesError,
    setUserAttributeError,
    unsetUserAttributeError,
    userAttributeLoaded
  };
};